import * as React from "react";
import { Formik } from "formik";
import {
	Icon,
	MessageBar,
	MessageBarType,
	Panel,
	PanelType,
	PrimaryButton,
	TextField,
	Toggle,
} from "@fluentui/react";
import { FormattedMessage, injectIntl, WrappedComponentProps } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { ErrorTypes } from "../../../../../../models/enums";
import { IApplicationState } from "../../../../../../models/interfaces/IApplicationState";
import { ITermSet } from "../../../../../../models/responseModels/taxonomy/ITermSet";
import { addTerm, resetError } from "../../../../../../store/taxonomy/actions";
import { IAddTerm } from "../../../../../../models/viewmodels/taxonomy/IAddTerm";
import { ITerm } from "../../../../../../models/responseModels/taxonomy/ITerm";

export interface IAddTermPanelProps {
	termSet: ITermSet;
	parentTerm?: ITerm;
	isOpen: boolean;
	onClose: () => void;
}

const AddTermPanel = (props: WrappedComponentProps & IAddTermPanelProps) => {
	const { termSet, isOpen, onClose, intl } = props;
	const dispatch = useDispatch();
	const taxonomySlice = useSelector((state: IApplicationState) => state.taxonomySlice);

	if (termSet === null || !isOpen) {
		return null;
	}

	const initialValues: IAddTerm = {
		name: "",
		isActive: true,
		termSetId: termSet.id,
		isSelectable: true,
		parentTermId: props.parentTerm ? props.parentTerm.id : null,
	};
	return (
		<Panel
			className="one-panel"
			isOpen={isOpen}
			type={PanelType.smallFixedFar}
			onDismiss={onClose}
			isLightDismiss
			onLightDismissClick={onClose}
			closeButtonAriaLabel="Close"
		>
			<section className="one-panel__panel-header">
				<div className="one-panel__panel-header--flex">
					<h1>
						<FormattedMessage id="taxonomy.term.new" defaultMessage="Nieuwe term" />
					</h1>

					<div className="one-panel__panel-close" onClick={onClose}>
						<Icon iconName="ChromeClose" />
					</div>
				</div>
			</section>
			{taxonomySlice.error.type === ErrorTypes.OnCreate && (
				<MessageBar
					messageBarType={MessageBarType.error}
					onDismiss={() => dispatch(resetError())}
				>
					{taxonomySlice.error.message}
				</MessageBar>
			)}
			<section className="one-panel__panel-content">
				<div className="ilx-form-wrapper">
					<Formik
						enableReinitialize
						initialValues={initialValues}
						onSubmit={(values) => {
							dispatch(addTerm(values));
						}}
						validationSchema={Yup.object().shape({
							name: Yup.string()
								.min(
									2,
									intl.formatMessage({
										id: "taxonomy.validation.name.minTwoChars",
										defaultMessage:
											"De naam moet ten minste twee tekens lang zijn.",
									})
								)
								.required(
									intl.formatMessage({
										id: "general.validation.required",
										defaultMessage: "Dit veld is verplicht.",
									})
								),
						})}
					>
						{(props) => {
							const {
								values,
								errors,
								handleChange,
								handleBlur,
								handleSubmit,
								setFieldValue,
							} = props;
							return (
								<form onSubmit={handleSubmit}>
									<TextField
										id="name"
										label={intl.formatMessage({
											id: "taxonomy.term.name.label",
											defaultMessage: "Naam",
										})}
										placeholder={intl.formatMessage({
											id: "taxonomy.term.name.placeholder",
											defaultMessage: "De naam van de term",
										})}
										type="text"
										required
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.name}
										errorMessage={errors.name}
										autoComplete={"off"}
										className={"ilx-text-field"}
									/>

									<Toggle
										checked={values.isSelectable}
										label={
											<div className="ilx-label-with-tooltip">
												<label>
													<FormattedMessage
														id="taxonomy.term.isSelectable"
														defaultMessage="Is selecteerbaar"
													/>
												</label>
												<span>
													<FormattedMessage
														id="taxonomy.term.isSelectable.description"
														defaultMessage="Deze term is te selecteren. Deze instelling heeft geen invloed op eventueel onderliggende termen."
													/>
												</span>
											</div>
										}
										className="ilx-toggle"
										onText={intl.formatMessage({
											id: "general.yes",
											defaultMessage: "Ja",
										})}
										offText={intl.formatMessage({
											id: "general.no",
											defaultMessage: "Nee",
										})}
										onChange={() =>
											setFieldValue("isSelectable", !values.isSelectable)
										}
									/>

									<Toggle
										checked={values.isActive}
										label={
											<div className="ilx-label-with-tooltip">
												<label>
													<FormattedMessage
														id="general.isActive"
														defaultMessage="In gebruik"
													/>
												</label>
												<span>
													<FormattedMessage
														id="taxonomy.term.isActive.description"
														defaultMessage="Deze term en eventueel onderliggenden termen zijn actief. Een inactieve term zorgt ervoor dat deze term en de onderliggende termen niet worden getoond in de picker."
													/>
												</span>
											</div>
										}
										className="ilx-toggle"
										onText={intl.formatMessage({
											id: "general.yes",
											defaultMessage: "Ja",
										})}
										offText={intl.formatMessage({
											id: "general.no",
											defaultMessage: "Nee",
										})}
										onChange={() => setFieldValue("isActive", !values.isActive)}
									/>

									<PrimaryButton
										text={intl.formatMessage({
											id: "general.create",
											defaultMessage: "Aanmaken",
										})}
										type="submit"
										disabled={
											taxonomySlice.isUpdatingOrCreating ||
											(Object.keys(errors).length !== 0 &&
												errors.constructor === Object)
										}
									/>
								</form>
							);
						}}
					</Formik>
				</div>
			</section>
		</Panel>
	);
};

export const AddTermPanelComponent = injectIntl(AddTermPanel);
